import React, { useState, useEffect } from 'react';
import { User, LogOut } from 'lucide-react';

/**
 * UserIndicator - Shows logged in user's name/email with click-to-dashboard
 * Appears in top-right corner on all pages when user is logged in
 * Includes logout functionality
 */
const UserIndicator = () => {
  const [user, setUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    // Check for user in localStorage (multiple possible keys)
    const userId = localStorage.getItem('elchme_user_id') || localStorage.getItem('workbook_user_id');
    const userName = localStorage.getItem('elchme_user_name') || localStorage.getItem('workbook_user_name');
    const userEmail = localStorage.getItem('elchme_user_email') || localStorage.getItem('workbook_user_email');
    
    if (userId && (userName || userEmail)) {
      setUser({
        id: userId,
        name: userName,
        email: userEmail
      });
    }
    
    // Listen for storage changes (e.g., login/logout in another tab)
    const handleStorageChange = () => {
      const newUserId = localStorage.getItem('elchme_user_id') || localStorage.getItem('workbook_user_id');
      const newUserName = localStorage.getItem('elchme_user_name') || localStorage.getItem('workbook_user_name');
      const newUserEmail = localStorage.getItem('elchme_user_email') || localStorage.getItem('workbook_user_email');
      
      if (newUserId && (newUserName || newUserEmail)) {
        setUser({ id: newUserId, name: newUserName, email: newUserEmail });
      } else {
        setUser(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (!user) return null;

  // Display name or email (truncated if needed)
  const displayName = user.name || user.email || 'User';
  const truncatedName = displayName.length > 20 ? displayName.substring(0, 18) + '...' : displayName;

  const handleLogout = () => {
    // Navigate to logout page
    window.location.href = '/auth/logout';
  };

  return (
    <div className="fixed top-3 right-3 z-[100]" data-testid="user-indicator">
      <div 
        className="relative"
        onMouseEnter={() => setShowMenu(true)}
        onMouseLeave={() => setShowMenu(false)}
      >
        {/* User Button */}
        <a
          href="https://belongingsessions.com/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10 hover:border-amber-500/30 hover:bg-amber-500/10 transition-all duration-300 group"
          title={`${user.email || user.name} - Click for Dashboard`}
        >
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500/30 to-purple-500/30 flex items-center justify-center">
            <User size={14} className="text-amber-400 group-hover:text-amber-300" />
          </div>
          <span className="text-white/70 text-xs sm:text-sm group-hover:text-amber-300 transition-colors max-w-[120px] sm:max-w-[150px] truncate">
            {truncatedName}
          </span>
        </a>

        {/* Dropdown Menu */}
        {showMenu && (
          <div className="absolute top-full right-0 mt-1 py-1 bg-black/80 backdrop-blur-md border border-white/10 rounded-lg shadow-xl min-w-[160px]">
            <a
              href="https://belongingsessions.com/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-amber-300 hover:bg-white/5 transition-colors"
            >
              <User size={14} />
              Dashboard
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white/70 hover:text-red-400 hover:bg-white/5 transition-colors w-full text-left"
            >
              <LogOut size={14} />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserIndicator;
